<template>
<div>
    <div ref="carousel" class="carousel" v-on:pointerdown="stopAutoAdvancing">
        <PP11 />
        <TMP3 />
        <SurveyScramble />
        <NaughtyPack />
        <ABTest test="megapicker-feature" :pct="0.10">
            <template v-slot:control>
                <Megapicker />
            </template>
            <template v-slot:alt>
                <Megapicker bg="alt" />
            </template>
        </ABTest>
        <TMP2 />
    </div>
    <div class="controls">
        <div
            class="arrow-button"
            role="button"
            tabIndex="0"
            :class="{'active': currentSlide > 0}"
            v-on:click="prevSlide"
            v-on:keypress.left="prevSlide"
        >
            <NuxtImg
                src="/images/icons/carousel-arrow.svg"
                class="left-arrow"
                alt="left"
            />
        </div>
        <div
            v-for="(slide, n) in slides"
            :key="n"
            role="button"
            tabIndex="0"
            class="slide-dot"
            :class="{'active': currentSlide === n}"
            v-on:click="jumpToSlide(n)"
            v-on:keypress.enter="jumpToSlide(n)"
        />
        <div
            class="arrow-button"
            role="button"
            tabIndex="0"
            :class="{'active': currentSlide < numSlides-1}"
            v-on:click="nextSlide"
            v-on:keypress.right="nextSlide"
        >
            <NuxtImg
                src="/images/icons/carousel-arrow.svg"
                class="right-arrow"
                alt="right"
            />
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import Megapicker from '$components/homepage/features/Megapicker.vue'
import TMP2 from '$components/homepage/features/TMP2.vue'
import ABTest from '$components/ABTest.vue'
import NaughtyPack from '$components/homepage/features/NaughtyPack.vue'
import SurveyScramble from '$components/homepage/features/SurveyScramble.vue'
import PP11 from '$components/homepage/features/PP11.vue'
import TMP3 from '~/components/homepage/features/TMP3.vue'

interface Slide {
    el: Element,
    left: number
}

const currentSlide = ref(0)
const slides = ref<Slide[]>([])
const slideIntervalId = ref<number>()

// TODO: Swap to useTemplateRef() after Vue 3.5+ upgrade
const carousel = ref<HTMLDivElement>(null)

const isManuallyAdvancing = ref(false)

const numSlides = computed(() => slides.value?.length || 0)

function stopAutoAdvancing() {
    window.clearInterval(slideIntervalId.value)
}

async function nextSlide() {
    stopAutoAdvancing()

    isManuallyAdvancing.value = true
    await setSlide(currentSlide.value + 1)
    isManuallyAdvancing.value = false
}

async function prevSlide() {
    stopAutoAdvancing()

    isManuallyAdvancing.value = true
    await setSlide(currentSlide.value - 1)
    isManuallyAdvancing.value = false
}

async function jumpToSlide(n: number) {
    stopAutoAdvancing()

    isManuallyAdvancing.value = true
    await setSlide(n)
    isManuallyAdvancing.value = false
}

async function centerSlide(n: number) {
    // scroll into view, but wait until the browser reports the scroll ends so
    // we don't update navigation controls for in-between slides by mistake
    let finishedScrolling: (value: void) => void
    const scrollingPromise = new Promise<void>((res) => {
        finishedScrolling = res
    })

    carousel.value.addEventListener('scrollend', () => {
        finishedScrolling()
    }, { once: true })

    carousel.value.scrollLeft = slides.value[n].left

    return scrollingPromise
}

function setSlide(n: number) {
    if (n < 0 || n >= numSlides.value) return Promise.resolve()

    currentSlide.value = n

    slides.value.forEach((slide: Slide, idx: number) => {
        if (n === idx) {
            slide.el.classList.add('active-slide')
            slide.el.classList.remove('inactive-slide')
        } else {
            slide.el.classList.remove('active-slide')
            slide.el.classList.add('inactive-slide')
        }
    })

    return centerSlide(n)
}

function setupSlides() {
    // clear out any previous slides, in case this is a resize
    slides.value.length = 0

    const features = carousel.value.children

    let total = 0
    for (let i = 0; i < features.length; i++) {
        const el = features[i]
        const rect = el.getBoundingClientRect()
        slides.value.push({
            el,
            left: total
        })
        total += rect.width + 20
    }

    void setSlide(0)
}

onMounted(() => {
    setupSlides()

    // auto advance
    slideIntervalId.value = window.setInterval(() => {
        let slide = currentSlide.value + 1
        if (slide >= numSlides.value) {
            slide = 0
        }
        void setSlide(slide)
    }, 8000)

    // observe swipes between slides for mobile users
    const features = Array.from(carousel.value.children)
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!isManuallyAdvancing.value && entry.isIntersecting) {
                const feature = entry.target
                currentSlide.value = features.indexOf(feature)
            }
        })
    }, {
        root: carousel.value,
        threshold: 0.75
    })
    features.forEach((feature) => {
        observer.observe(feature)
    })

    window.addEventListener('resize', setupSlides)
})

onUnmounted(() => {
    stopAutoAdvancing()
    window.removeEventListener('resize', setupSlides)
})
</script>

<style lang="scss" scoped>
@import "../../styles/kit.scss";

.carousel {
    margin: 32px 0;
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    gap: 20px;
    overflow-x:scroll;
    scroll-behavior: smooth;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
    @include mq-medium {
        margin: 24px 0;
    }
    @include mq-small {
        margin: 16px 0;
    }
    @include mq-xsmall {
        margin: 16px 0;
        gap: 16px;
    }
    @include mq-large-and-more {
        margin: 32px auto;
    }

    .carousel-item {
        aspect-ratio: 2.2;
        border-radius: 16px;
        flex-shrink: 0;
        scroll-snap-align: center;
        overflow: hidden;
        height: 100%;
        width: 88vw;
        max-width: 1280px;

        &:first-child {
            margin-left: 6vw;
        }
        &:last-child {
            margin-right: 6vw;
        }

        @include mq-small {
            aspect-ratio: 2.0;
            min-height: 320px;
        }
        @include mq-xsmall {
            aspect-ratio: 0.75;
            min-height: 560px;
        }

        .bg-video {
            width: 100%;
        }

        @include mq-large-and-more {
            transition: opacity 750ms, scale 750ms;
            &:first-child {
                margin-left: calc(100%/4);
            }
            &:last-child {
                margin-right: calc(100%/4);
            }
        }
    }

    .inactive-slide {
        @include mq-large-and-more {
            opacity: 0.3;
            scale: 90%;
        }
    }
}

.controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 15px;

    @include mq-xsmall {
        margin-bottom: 16px;
    }

    .left-arrow {
        transform: scaleX(-1);
    }

    .arrow-button {
        margin: 0 20px;
        opacity: 0.4;
        &.active {
            opacity: 1;
            cursor: pointer;
            transition: all 0.1s ease-out;
            &:hover {
                scale: 1.15 1.15;
            }
        }
    }

    .slide-dot {
        height: 15px;
        width: 15px;
        background-color: #71768E;
        border-radius: 50%;
        display: inline-block;
        transition: all 0.1s ease-out;
        cursor: pointer;
        &:hover {
            scale: 1.15 1.15;
        }

        &.active {
            background-color: #B2BBFF;
            filter: drop-shadow(0px 0px 7.452px #B2BBFF);
            &:hover {
                scale: 1 1;
                cursor: unset;
            }
        }
    }
}
</style>
